import { useState, useEffect, useMemo } from "react";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";

const TIERS = [
  {
    value: 2500,
    label: "2 500 Ft / hó",
    perks: ["Balatonos nyakpánt"],
  },
  {
    value: 5000,
    label: "5 000 Ft / hó",
    perks: ["Évadonként egyszeri 10% jegyárkedvezmény (min. 12 hónap támogatás)", "Balatonos bögre"],
  },
  {
    value: 7500,
    label: "7 500 Ft / hó",
    perks: ["Évadonként egyszeri 10% jegyárkedvezmény (min. 12 hónap támogatás)", "Balatonos póló"],
  },
  {
    value: 10000,
    label: "10 000 Ft / hó",
    perks: ["Évadonként 2 ingyen jegy (min. 12 hónap támogatás)", "Balatonos ajándékcsomag"],
    highlight: false,
  },
  {
    value: 15000,
    label: "15 000 Ft / hó",
    perks: ["Évadonként 4 ingyen jegy (min. 12 hónap támogatás)", "Balatonos ajándékcsomag"],
  },
];

function SupportersCounter() {
  const [count, setCount] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const r = await fetch("/api/supportersCount");
        if (!r.ok) throw new Error("count failed");
        const { supporters } = await r.json(); // { supporters: number }
        if (mounted) setCount(typeof supporters === "number" ? supporters : null);
      } catch (err) {
        console.warn("supporters/count failed:", err);
        if (mounted) setCount(0);
      }
    }

    load();
    const id = setInterval(load, 60000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [setCount]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 left-6 z-40 rounded-full px-5 py-3 shadow-lg ring-1 ring-black/5
                   bg-sky-600 text-white hover:bg-sky-700 transition"
        aria-label="Támogatók számlálója"
      >
        {count === null ? "Támogatók" : `Támogatók: ${count.toLocaleString("hu-HU")}`}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <h3 className="text-xl font-bold">Támogatóink</h3>
              <button
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-1 text-sm hover:bg-gray-100"
              >
                Bezár
              </button>
            </div>

            <p className="mb-4 text-gray-700">
              Ennyien csatlakoztak eddig a „Sok kicsi Folk-ra megy!” közösségéhez.
              Köszönjük, hogy együtt őrizzük és éltetjük a magyar kultúrát!
            </p>

            <div className="mb-6 rounded-xl bg-sky-50 p-4 text-center">
              <div className="text-4xl font-extrabold">
                {count === null ? "—" : count.toLocaleString("hu-HU")}
              </div>
              <div className="text-sm text-sky-800 mt-1">{count === null ? "betöltés…" : "aktív havi támogatás"}</div>
            </div>

            <button
              onClick={() => setOpen(false)}
              className="w-full rounded-md bg-sky-600 px-6 py-3 font-semibold text-white
                         hover:bg-sky-700"
            >
              Rendben
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default function IndividualSupport() {
  const [selected, setSelected] = useState(TIERS[1]);
  const [email, setEmail] = useState("");
  const [accept, setAccept] = useState(false);
  const [loading, setLoading] = useState(false);

  const buttonDisabled = useMemo(
    () => !email || !accept || loading,
    [email, accept, loading]
  );

  async function handleSubscribe() {
    if (!accept) return toast.error("Kérjük, fogadd el a feltételeket!");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return toast.error("Érvényes e-mail címet adj meg!");
    }
    setLoading(true);
    try {
      const r   = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: selected.value, email }),
      });
      const { gatewayUrl } = await r.json();
      window.location.href = gatewayUrl;
    } catch {
      toast.error("Nem sikerült csatlakozni a fizetési oldalhoz.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="mx-auto max-w-3xl px-4 pb-12 space-y-10">
      <SupportersCounter />

      <header className="text-center space-y-3">
        <img
          src="/images/BTE_LOGO.png"
          alt="Balaton Táncegyüttes logó"
          className="mx-auto object-contain"
        />
        <h1 className="text-2xl md:text-3xl font-extrabold leading-tight">
          Sok kicsi Folk-ra megy!
        </h1>
        <p className="text-sky-700 font-medium">Civilek a Balaton-ért!</p>
      </header>

      <section className="mx-auto max-w-2xl space-y-4 text-gray-700 leading-relaxed">
        <p>
          „Csatlakozz hozzánk Te is: a Balaton Táncegyüttes és a magyar kultúra
          támogatói közösségéhez most egy egyszerű, átlátható felületen, ahol egy gyors
          regisztráció után könnyedén eljuttathatod havi támogatásodat felénk.”
        </p>
        <p>
          Együttesünk számára a kiszámítható működés és a hosszútávú tervezhetőség
          jelenti a legnagyobb biztonságot. Ezért találtuk ki ezt a támogatási formát,
          ahol minden támogatni szándékozó gyorsan és egyszerűen, a rendszeres
          hozzájárulásával patronálni tudja munkánkat. Önnek egy kis figyelmesség —
          nekünk a megmaradásunk és a szakmai munkánk folytatásának záloga.
        </p>
      </section>

      {/* Tier választó kártyák */}
      <section className="grid gap-4 sm:grid-cols-2">
        {TIERS.map(tier => {
          const active = selected.value === tier.value;
          return (
            <button
              type="button"
              key={tier.value}
              onClick={() => setSelected(tier)}
              className={[
                "group rounded-2xl border p-5 text-left transition hover:shadow-md",
                active ? "border-sky-600 ring-2 ring-sky-200" : "border-gray-200",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-bold">{tier.label}</div>
                  {tier.highlight && (
                    <div className="inline-block mt-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700">
                      Népszerű
                    </div>
                  )}
                </div>
                <div
                  className={[
                    "mt-1 h-5 w-5 rounded-full border-2",
                    active ? "border-sky-600 bg-sky-600" : "border-gray-300",
                  ].join(" ")}
                  aria-hidden
                />
              </div>
              <ul className="mt-3 space-y-1 text-sm text-gray-700">
                {tier.perks.map(p => (
                  <li key={p} className="flex items-start gap-2">
                    <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-sky-600" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </section>

      {/* Email + ÁSZF */}
      <section className="space-y-4">
        <input
          type="email"
          required
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="E-mail címed"
          className="w-full rounded-md border px-3 py-2"
        />

        <label className="flex items-start gap-3 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0"
            checked={accept}
            onChange={e => setAccept(e.target.checked)}
            required
          />
          <span>
            Elfogadom az{" "}
            <Link to="/terms-of-use" target="_blank" className="underline text-primary">
              Általános Szerződési Feltételeket
            </Link>{" "}
            és az{" "}
            <Link to="/privacy-policy" target="_blank" className="underline text-primary">
              Adatkezelési tájékoztatót
            </Link>.
          </span>
        </label>
      </section>

      {/* Fizetés CTA */}
      <section className="space-y-3">
        <p className="text-xs text-gray-600 leading-snug">
          A „Támogatom” gomb megnyomásával jóváhagyod, hogy bankkártyádat a Barion
          tokenizálja, és a választott összeget havonta automatikusan levonja.
          A lemondás menetét az ÁSZF 5. pontja ismerteti. ApplePay csak egyszeri
          támogatás esetén érhető el.
        </p>

        <button
          disabled={buttonDisabled}
          onClick={handleSubscribe}
          className="w-full rounded-md bg-sky-600 px-8 py-3 font-semibold text-white
                     hover:bg-sky-700 disabled:opacity-50"
        >
          {loading ? "Átirányítás…" : "Támogatom"}
        </button>

        <img
          src="./barion/EU-Small-nobg-lightmode.png"
          alt="Barion – Biztonságos fizetés"
          className="mx-auto mt-8"
        />
        <p className="text-center text-xs text-gray-600 mt-2 max-w-xs mx-auto">
          A fizetés a Barion rendszerén keresztül történik. A bankkártya-adatokat nem a
          szolgáltató, hanem a Barion kezeli. A Barion Payment Zrt. a Magyar Nemzeti Bank
          felügyelete alatt álló intézmény, engedélyszám: H-EN-I-1064/2013.
        </p>
      </section>
    </main>
  );
}
